import type { Dispatch } from 'react';
import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';

/*
 * Custom pagination component implemented with Tailwind.
 *
 * Responsibilities:
 *    1. Display the current state of pageContext (provided by caller).
 *    2. Call setPageContext (provided by caller) on user interaction.
 *    3. Run an optional handler on page change (provided by caller).
 */

type PaginationProps = {
   pageContext: {
      type: string;
      curPage: number;
      totalPages: number;
      prevPage: boolean;
      nextPage: boolean;
   };
   setPageContext: Dispatch<any>;
   // FIXME: Don't use `Function` as a type. The `Function` type accepts any function-like value.
   // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
   handler?: Function;
   className?: string;
};

const Pagination = ({ pageContext, setPageContext, handler, className }: PaginationProps) => {
   function updatePageContext(diff: number) {
      const newPage = pageContext.curPage + diff;
      const newPrevPage = newPage > 1;
      const newNextPage = newPage < pageContext.totalPages;

      if (newPage > 0 && newPage <= pageContext.totalPages) {
         setPageContext({
            ...pageContext,
            curPage: newPage,
            prevPage: newPrevPage,
            nextPage: newNextPage,
         });
         if (handler) {
            handler();
         }
      }
   }

   const pagerButton = cn(
      'inline-flex items-center gap-1 h-[25px] my-auto px-3 text-gray-600 font-semibold bg-transparent border-none cursor-pointer hover:text-brand-500'
   );

   return (
      <div
         className={cn(
            'flex justify-between bg-gray-100 border border-gray-300 overflow-hidden',
            className
         )}
      >
         <div className="flex w-1/3 justify-start">
            {pageContext.prevPage ? (
               <button
                  type="button"
                  className={pagerButton}
                  onClick={() => {
                     updatePageContext(-1);
                  }}
               >
                  <FaIcon name="angle-left" className="size-[1em]" aria-hidden="true" />
                  {_js('Previous')}
               </button>
            ) : null}
         </div>
         <div className="flex w-1/3 justify-center">
            <p className="my-auto font-semibold">
               {_js('Page %1 of %2', String(pageContext.curPage), String(pageContext.totalPages))}
            </p>
         </div>
         <div className="flex w-1/3 justify-end">
            {pageContext.nextPage ? (
               <button
                  type="button"
                  className={pagerButton}
                  onClick={() => {
                     updatePageContext(1);
                  }}
               >
                  {_js('Next')}
                  <FaIcon name="angle-right" className="size-[1em]" aria-hidden="true" />
               </button>
            ) : null}
         </div>
      </div>
   );
};

export default Pagination;
